import React from 'react';
import { useEditorStore } from '../../stores/editorStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { Canvas } from '../Canvas/Canvas';

interface ContentAreaProps {
  isPresentation?: boolean;
  scale?: number;
}

// Solid base colors for each theme (YouTube-friendly, no gradient banding)
const baseColors: Record<string, string> = {
  'default': '#1a0a00', // Very dark orange-brown
  'blue': '#0a0a1a',    // Very dark blue
  'purple': '#0f0a1a',  // Very dark purple
  'green': '#0a1a0a',   // Very dark green
  'red': '#1a0a0a',     // Very dark red
  'neutral': '#0f0f0f', // Near black
};

// Accent colors for subtle tint
const accentColors: Record<string, string> = {
  'default': '#ff6b00',
  'blue': '#3b82f6',
  'purple': '#8b5cf6',
  'green': '#22c55e',
  'red': '#ef4444',
  'neutral': '#6b7280',
};

// SVG noise texture as data URI (small, tileable)
const noiseDataUri = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

export const ContentArea: React.FC<ContentAreaProps> = ({ isPresentation = false, scale = 1 }) => {
  const { currentEpisode, currentSectionIndex } = useEditorStore();
  const { settings } = useSettingsStore();
  const currentSection = currentEpisode?.sections[currentSectionIndex];

  const preset = currentSection?.background || (typeof settings.defaultBackground === 'string' ? settings.defaultBackground : 'default');
  const baseColor = baseColors[preset] || baseColors['default'];
  const accentColor = accentColors[preset] || accentColors['default'];

  return (
    <div 
      className="relative w-full h-full rounded-lg overflow-hidden"
      style={{ backgroundColor: baseColor }}
    >
      {/* Subtle color accent in corner */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 0% 0%, ${accentColor}15 0%, transparent 50%)`,
        }}
      />
      
      {/* Noise texture overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: noiseDataUri,
          backgroundRepeat: 'repeat',
        }}
      />
      
      {/* Vignette */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 100%)',
        }}
      />
      
      <Canvas hideControls={isPresentation} scale={scale} />
      

    </div>
  );
};
